import cookie from 'vue-cookie'
import $store from '@/vuex/store'
import config from '@/configurations/config-environment'
import { BaseService } from '../baseService'
import ExportProjectFieldUpdate from '@/components/export/class/ExportProjectFieldUpdate'
import { ProjectService } from '../DataWS/projectService'

const importExportOrchestrator = config.apis.importExportOrchestrator
const projectService = new ProjectService()

const LEGACY_RECORD_TYPES = {
  Error: 'ERROR',
  Warning: 'ATTENTION',
  Information: 'SUCCES',
  Success: 'SUCCES'
}

export class Exporter extends BaseService {
  async setExportProjects(exportProjects) {
    for (const project of exportProjects) {
      await this.setExportFiles(project.PrID, project.selectedExportConfig, project.conversionContext)
    }
  }

  async setExportFiles(PrID, selectedExportConfig, conversionContext) {
    const authorisation = this.getAuthorizationObject()

    try {
      const responseData = await this.exportProject(PrID, authorisation, selectedExportConfig, conversionContext)
      this.saveExportProject(this.createExportResponse(responseData, selectedExportConfig.useCompression), PrID)
    } catch (error) {
      console.error(error)
      this.saveExportProject(this.createFailedExportResponse(error), PrID)
    }
  }

  async exportProject(PrID, authorisation, selectedExportConfig, conversionContext) {
    const streamlineProjectId = await this.getStreamlineProjectId(PrID)
    const token = cookie.get('idToken')

    const data = {
      outputFormatType: selectedExportConfig.outputFormatType,
      outputVersion: selectedExportConfig.outputVersion,
      languageCode: this.getLangCodeLongFromShort(authorisation.Language),
      exportParameters: this.createExportParameters(conversionContext),
      useCompression: Boolean(selectedExportConfig.useCompression),
      mergeIntoSingleJson: Boolean(selectedExportConfig.mergeIntoSingleJson)
    }

    const url = `${importExportOrchestrator}/import-export/export-project/${authorisation.Licensenumber}/${streamlineProjectId}`

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(data)
    })

    if (!response.ok) {
      throw new Error(`Export failed for project ${streamlineProjectId}. Status: ${response.status}`)
    }

    return response.json()
  }

  // The current project is only loaded when exporting from within a project; an export started
  // from the projects overview has to fetch the project to learn its Streamline identifier.
  async getStreamlineProjectId(PrID) {
    const currentProject = $store.state.currentProject
    let streamlineProjectId = currentProject && String(currentProject.PrID) === String(PrID)
      ? currentProject.PrStreamlineGUID
      : null

    if (!streamlineProjectId) {
      streamlineProjectId = await this.fetchStreamlineProjectId(PrID)
    }

    if (!streamlineProjectId) {
      throw new Error(`Export failed because the Streamline identifier of project ${PrID} is unknown.`)
    }

    return streamlineProjectId
  }

  async fetchStreamlineProjectId(PrID) {
    const response = await projectService.getSingleProject(PrID)
    if (!response.data || response.data.ResultCode !== 'Export_Succes') return null

    const projectResponse = JSON.parse(response.data.Files[0].FileContent)
    return projectResponse.VeldData.tblProjects.PrStreamlineGUID || null
  }

  // The pre-export checks store their answers flat, while the converter expects them nested under
  // exportParameters and rejects any other property.
  createExportParameters(conversionContext) {
    if (!conversionContext) return undefined

    const { broQualityRegime, broCorrectionReason, broIdRequirement } = conversionContext
    if (!broQualityRegime && !broCorrectionReason && !broIdRequirement) return undefined

    return { broQualityRegime, broCorrectionReason, broIdRequirement }
  }

  createExportResponse(responseData, useCompression) {
    const records = (responseData.log && responseData.log.records) || []
    const files = (responseData.files || []).map(file => ({
      FileContent: useCompression ? file.contentBase64 : this.decodeBase64AsText(file.contentBase64)
    }))
    const hasErrors = records.some(record => record.type === 'Error')

    return {
      ResultCode: hasErrors ? 'Export_Succes_With_Messages' : 'Export_Succes',
      Files: files,
      ExportLog: this.createExportLog(records)
    }
  }

  createFailedExportResponse(error) {
    const records = [
      {
        Title: 'Export',
        Type: 'ERROR',
        Message: error.message,
        Timestamp: new Date().toISOString()
      }
    ]

    return {
      ResultCode: 'Export_UNKNOWN_ERROR',
      Files: [],
      ExportLog: JSON.stringify({ ArrayOfExportLogRecord: { ExportLogRecord: records } })
    }
  }

  decodeBase64AsText(contentBase64) {
    const binaryContent = atob(contentBase64)
    const bytes = Uint8Array.from(binaryContent, character => character.charCodeAt(0))

    return new TextDecoder('utf-8').decode(bytes)
  }

  createExportLog(records) {
    if (records.length === 0) return null

    const exportLogRecords = records.map(record => ({
      Title: record.objectContext || record.context,
      Type: LEGACY_RECORD_TYPES[record.type] || 'SUCCES',
      Message: record.parsedMessage || record.message,
      Timestamp: record.created
    }))

    return JSON.stringify({ ArrayOfExportLogRecord: { ExportLogRecord: exportLogRecords } })
  }

  saveExportProject(response, PrID) {
    const exportResponseFieldValue = new ExportProjectFieldUpdate('exportResponse', response, PrID)
    $store.commit('updateExportProjectBindingKey', exportResponseFieldValue)
  }
}
